import { useState, type FormEvent } from 'react';
import { Pencil } from 'lucide-react';

import { ErroApi } from '@/nucleo/auth/cliente-api';
import { useAuth } from '@/nucleo/auth/useAuth';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/base/dialog';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';

import { salvarMeusDados } from './dados';

/** A mesma regra da API (`apps/api/src/modulos/eu/eu.dto.ts`). */
const PARTE_DO_NOME = /^\p{L}[\p{L}\p{M}' .-]*$/u;
const TAMANHO_MAXIMO = 80;

const limpar = (valor: string) => valor.trim().replace(/\s+/g, ' ');

function erroDaParte(valor: string, rotulo: 'nome' | 'sobrenome'): string | null {
  const limpo = limpar(valor);
  if (!limpo) return `Informe o ${rotulo}.`;
  if (limpo.length > TAMANHO_MAXIMO) return `Use no máximo ${TAMANHO_MAXIMO} caracteres.`;
  if (!PARTE_DO_NOME.test(limpo)) return 'Use só letras, espaço, apóstrofo e hífen.';
  return null;
}

/** "Maria de Souza Lima" vira nome "Maria" e sobrenome "de Souza Lima". */
function separar(nomeCompleto: string | null): { nome: string; sobrenome: string } {
  const [nome = '', ...resto] = (nomeCompleto ?? '').trim().split(/\s+/);
  return { nome, sobrenome: resto.join(' ') };
}

/**
 * "Editar meus dados" em "Meu perfil" (pedido do dono, 05/10/2026): a própria
 * pessoa troca nome e sobrenome. A API (`PATCH /eu/dados`) grava no Keycloak e
 * no espelho do sistema, com registro na trilha; o diálogo só fecha quando ela
 * confirma, e o nome muda no cabeçalho e no perfil na mesma hora.
 *
 * O e-mail não muda por aqui: é o endereço do segundo fator e dos avisos, e a
 * troca é pedida à administração.
 */
export function EditarMeusDados() {
  const { usuario, aplicarNovoNome } = useAuth();
  const [aberto, setAberto] = useState(false);
  const [nome, setNome] = useState('');
  const [sobrenome, setSobrenome] = useState('');
  const [tentou, setTentou] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  if (!usuario) return null;

  const erroDoNome = tentou ? erroDaParte(nome, 'nome') : null;
  const erroDoSobrenome = tentou ? erroDaParte(sobrenome, 'sobrenome') : null;

  function abrir() {
    const partes = separar(usuario?.nome ?? null);
    setNome(partes.nome);
    setSobrenome(partes.sobrenome);
    setTentou(false);
    setErro(null);
    setAberto(true);
  }

  function fechar() {
    if (!enviando) setAberto(false);
  }

  async function salvar(evento: FormEvent) {
    evento.preventDefault();
    setTentou(true);
    if (erroDaParte(nome, 'nome') || erroDaParte(sobrenome, 'sobrenome')) return;
    setEnviando(true);
    setErro(null);
    try {
      const salvo = await salvarMeusDados({ nome: limpar(nome), sobrenome: limpar(sobrenome) });
      await aplicarNovoNome?.(salvo.nome);
      setEnviando(false);
      setAberto(false);
    } catch (falha) {
      setEnviando(false);
      setErro(
        falha instanceof ErroApi ? falha.aviso : 'Não foi possível salvar agora. Tente de novo.',
      );
    }
  }

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={abrir}>
        <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
        Editar meus dados
      </Button>
      <Dialog open={aberto} onOpenChange={(v) => !v && fechar()}>
        <DialogContent>
          <form noValidate onSubmit={(e) => void salvar(e)} className="grid gap-4">
            <DialogHeader>
              <DialogTitle>Editar meus dados</DialogTitle>
              <DialogDescription>
                O nome aparece no cabeçalho, no seu perfil e nos registros do sistema.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="meus-dados-nome">Nome</Label>
              <Input
                id="meus-dados-nome"
                value={nome}
                maxLength={TAMANHO_MAXIMO}
                autoComplete="given-name"
                aria-invalid={erroDoNome ? true : undefined}
                aria-describedby={erroDoNome ? 'meus-dados-nome-erro' : undefined}
                onChange={(e) => setNome(e.target.value)}
              />
              {erroDoNome && (
                <p id="meus-dados-nome-erro" className="text-xs font-medium text-destructive-text">
                  {erroDoNome}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="meus-dados-sobrenome">Sobrenome</Label>
              <Input
                id="meus-dados-sobrenome"
                value={sobrenome}
                maxLength={TAMANHO_MAXIMO}
                autoComplete="family-name"
                aria-invalid={erroDoSobrenome ? true : undefined}
                aria-describedby={erroDoSobrenome ? 'meus-dados-sobrenome-erro' : undefined}
                onChange={(e) => setSobrenome(e.target.value)}
              />
              {erroDoSobrenome && (
                <p
                  id="meus-dados-sobrenome-erro"
                  className="text-xs font-medium text-destructive-text"
                >
                  {erroDoSobrenome}
                </p>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              O e-mail não muda por aqui: para trocá-lo, peça à administração do sistema.
            </p>
            {erro && (
              <Alert variant="destructive">
                <AlertDescription>{erro}</AlertDescription>
              </Alert>
            )}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={fechar} disabled={enviando}>
                Cancelar
              </Button>
              <Button type="submit" disabled={enviando}>
                {enviando ? 'Aguarde…' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
